import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { lt } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import ParityOtaParityLink from 'lumen/components/parity/ota-parity-link';
import add from 'lumen/helpers/add';
import type { Period } from 'lumen/models';
import type { PeriodParity } from 'lumen/models/period';
import type {
  HotelAndMarketServiceWithHotel,
  HotelOrMarket,
} from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { filterMajorOTAs } from 'lumen/utils/parities-filter';
import { formatParity, sortParities } from 'lumen/utils/parity-utils';

const MAX_NB_OF_OTAS_IN_CELL = 4;

export interface TdLossOnMetaColumn {
  availableOTAs: Ota[];
  hotel: HotelOrMarket | null;
  isParityAnalytics: boolean;
  otaId?: string;
}

interface ParityTablesCellsTdLossOnMetaSignature {
  Args: {
    cellData: Period;
    column: TdLossOnMetaColumn;
  };
}

export default class ParityTablesCellsTdLossOnMeta extends Component<ParityTablesCellsTdLossOnMetaSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRates() {
    const { cellData, column } = this.args;

    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      otaIds,
      cellData.paritiesPOSDict,
      (parity: PeriodParity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { channel, currency, extract_datetime, ota } = baseRate;

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        channel,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        extractDatetimeFromNow: dayjs.utc(extract_datetime).fromNow(),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        extract_datetime,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        rate: this.rates.formatRate(baseRate, this.hotel) as string,
        rateUnconverted: [
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.rates.formatRateObject(baseRate, false),
          currency,
        ].join(' '),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        source: this.ota.otaIdToPrettyName(ota),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        title: this.ota.otaIdToPrettyName(channel),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.period.day;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChannels() {
    return (this.parityDataPerOta as PeriodParity[])
      ?.filter((parity) => !parity.is_out_of_sync)
      .sort(sortParities)
      .slice(0, 15)
      .filter((parity) => parity.value !== 0 && parity.position_to_baserate < 0)
      .map((parity) => formatParity(this.ota, this.rates, this.hotel, parity));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    // Hotel is passed as an argument in market/parity/insight/historical
    return this.args.column.hotel ?? this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLongList() {
    return this.formattedChannels?.length > MAX_NB_OF_OTAS_IN_CELL;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPast() {
    if (!this.day.date) {
      return false;
    }
    return this.day.date.isBefore(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    if (!this.day.date) {
      return false;
    }

    return this.day.date.isSame(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxPersons() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['maxPersons'] ??
        DEFAULT_QP_VALUES.application.maxPersons,
    );
  }

  /**
   * Parity data for the current cell based on the OTA of the column
   * It's an object when the column is for only one OTA
   * It's an array when the column is for multiple OTAs
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityDataPerOta() {
    if (this.period.paritiesDict && this.args.column.otaId) {
      return this.period.paritiesDict[this.args.column.otaId] as
        | PeriodParity
        | PeriodParity[];
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOTAParityLink() {
    return !this.isPast && !this.args.column.isParityAnalytics;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get slicedChannels() {
    return this.formattedChannels.slice(0, MAX_NB_OF_OTAS_IN_CELL);
  }

  // quick admin only way to check to full rate object of the cell
  @action onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      console.group(
        `Debugging info loss channels on metasearch: - ${this.day?.date.format('ddd l')}`,
      );

      const debugInfo = (this.parityDataPerOta as PeriodParity[])
        ?.sort(sortParities)
        .slice(0, 15)
        .filter(
          (parity) => parity.value !== 0 && parity.position_to_baserate < 0,
        );

      if (console.table) {
        console.table(debugInfo);
      }
    }
  }

  <template>
    <div
      class="flex flex-row truncate {{if this.isToday 'td-today'}}"
      role="tooltip"
      {{on "dblclick" this.onDoubleClick}}
    >
      <p class="truncate">
        {{#each this.slicedChannels as |channel index|}}
          <span
            data-event-id="parity-table-metasearch-redirect-link"
            data-event-value={{channel.linkoutSource}}
          >
            {{#if this.showOTAParityLink}}
              <ParityOtaParityLink
                @hotel={{this.hotel}}
                @ota={{channel.linkoutSource}}
                @period={{this.period}}
                @persons={{this.maxPersons}}
                @value={{channel.title}}
              />
            {{else}}
              {{channel.title}}
            {{/if}}
          </span>
          {{#if (lt (add index 1) this.slicedChannels.length)}}
            ,&nbsp;
          {{/if}}
        {{else}}
          <p>{{t "parity.no_issues"}}</p>
        {{/each}}
        {{#if this.isLongList}}
          ...
        {{/if}}
      </p>

      <EmberTooltip
        @delay={{25}}
        @side="bottom"
        @tooltipClass="base-tooltip rates-table-tooltip display-over-modal"
      >
        <div class="rates-table-tooltip-container">
          <table cellspacing="0">
            <thead>
              <tr>
                <th>{{t "parity.channel"}}</th>
                <th>{{t "parity.lowest_rate"}}</th>
                <th>{{t "parity.loss"}}</th>
                <th>{{t "parity.source"}}</th>
              </tr>
            </thead>
            <tbody>
              {{#each this.formattedChannels as |channel|}}
                <tr>
                  <td>{{channel.title}} </td>
                  <td> {{channel.rate}}</td>
                  <td class="parity-loss">
                    {{channel.loss}}
                  </td>
                  <td> {{channel.source}} </td>
                </tr>
              {{/each}}
              {{#each this.baseRates as |baseRate|}}
                <tr>
                  <td>{{baseRate.title}}</td>
                  <td>{{baseRate.rate}}</td>
                  <td></td>
                  <td>{{baseRate.source}}</td>
                </tr>
              {{/each}}
            </tbody>
          </table>
        </div>
      </EmberTooltip>
    </div>
  </template>
}
